import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, extractError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ImageUpload from "@/components/ImageUpload";
import { Plus, Pencil, Trash2, MapPin, Star, ShieldCheck, ShieldAlert, ShieldQuestion } from "lucide-react";

const empty = {
  name: "", logo_url: "", country: "", city: "", address: "", phone: "", email: "",
  description: "", hours: "", verified: true, active: true, owner_user_id: "",
  rating: 0, reviews_count: 0,
  kyc_status: "pending", kyc_rccm_url: "", kyc_rib_url: "", kyc_id_doc_url: "",
  kyc_partnership_contract_url: "", kyc_notes: "",
  cancellation_policy: "moderate", cancellation_custom_text: "",
};

const KYC_META = {
  approved: { label: "Approuvée", icon: ShieldCheck, color: "bg-[#dcfce7] text-[#0e7a3f]" },
  pending: { label: "En attente", icon: ShieldQuestion, color: "bg-amber-100 text-amber-800" },
  rejected: { label: "Rejetée", icon: ShieldAlert, color: "bg-red-100 text-red-700" },
};

const CANCEL_POLICIES = [
  { key: "flexible", label: "Flexible — Annulation gratuite 24h avant" },
  { key: "moderate", label: "Modérée — Annulation gratuite 5 jours avant" },
  { key: "strict", label: "Stricte — Aucun remboursement" },
  { key: "custom", label: "Personnalisée" },
];

export default function AdminAgencies() {
  const [items, setItems] = useState([]);
  const [agencyUsers, setAgencyUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/agencies").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/users").then((r) => setAgencyUsers(r.data.filter((u) => u.role === "agency_partner"))).catch(() => {});
  }, []);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    try {
      const payload = {
        ...form,
        rating: Math.max(0, Math.min(5, parseFloat(form.rating) || 0)),
        reviews_count: Math.max(0, parseInt(form.reviews_count) || 0),
      };
      if (editId) await api.put(`/agencies/${editId}`, payload);
      else await api.post("/agencies", payload);
      toast.success(editId ? "Agence mise à jour" : "Agence créée");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(extractError(e)); }
  };
  const edit = (a) => { setEditId(a.id); setForm({ ...empty, ...a }); setOpen(true); };
  const remove = async (id) => { if (!window.confirm("Supprimer cette agence et ses véhicules ?")) return; await api.delete(`/agencies/${id}`); toast.success("Agence supprimée"); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-black text-[#0a1e3f]">Agences partenaires</h1>
          <p className="text-sm text-gray-500 mt-1">Gérez le réseau d'agences partenaires.</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditId(null); setForm(empty); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-agency-btn" className="mbk-btn-green text-white rounded-full"><Plus className="h-4 w-4 mr-1" /> Nouvelle agence</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl text-[#0a1e3f]">{editId ? "Modifier" : "Créer"} une agence</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
              <F l="Nom"><Input data-testid="ag-name" value={form.name} onChange={(e) => upd("name", e.target.value)} /></F>
              <div><Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">Logo de l'agence</Label><div className="mt-1.5"><ImageUpload value={form.logo_url} onChange={(v) => upd("logo_url", v)} label="Logo" testId="ag-logo" /></div></div>
              <F l="Pays"><Input data-testid="ag-country" value={form.country} onChange={(e) => upd("country", e.target.value)} /></F>
              <F l="Ville"><Input data-testid="ag-city" value={form.city} onChange={(e) => upd("city", e.target.value)} /></F>
              <F l="Adresse"><Input value={form.address} onChange={(e) => upd("address", e.target.value)} /></F>
              <F l="Téléphone"><Input value={form.phone} onChange={(e) => upd("phone", e.target.value)} /></F>
              <F l="Email"><Input value={form.email} onChange={(e) => upd("email", e.target.value)} /></F>
              <F l="Horaires"><Input value={form.hours} onChange={(e) => upd("hours", e.target.value)} placeholder="Lun-Sam 8h-18h" /></F>
              <F l="Note moyenne (0 à 5)"><Input data-testid="ag-rating" type="number" step="0.1" min="0" max="5" value={form.rating} onChange={(e) => upd("rating", e.target.value)} placeholder="4.7" /></F>
              <F l="Nombre d'avis"><Input data-testid="ag-reviews-count" type="number" min="0" value={form.reviews_count} onChange={(e) => upd("reviews_count", e.target.value)} placeholder="128" /></F>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">Compte utilisateur agence (propriétaire)</Label>
                <div className="mt-1.5">
                  <Select value={form.owner_user_id || "none"} onValueChange={(v) => upd("owner_user_id", v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="ag-owner"><SelectValue placeholder="Aucun (l'admin gère)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun (l'admin gère)</SelectItem>
                      {agencyUsers.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.full_name} — {u.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">Lie cette agence à un compte "Agence partenaire" qui ne verra que ses propres données.</p>
                </div>
              </div>
              <div className="md:col-span-2"><F l="Description"><Textarea rows={3} value={form.description} onChange={(e) => upd("description", e.target.value)} /></F></div>

              <div className="md:col-span-2 border-t pt-4">
                <div className="text-xs uppercase tracking-widest text-[#0e7a3f] font-bold mb-3">Politique d'annulation</div>
                <Select value={form.cancellation_policy || "moderate"} onValueChange={(v) => upd("cancellation_policy", v)}>
                  <SelectTrigger data-testid="ag-cancel-policy"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CANCEL_POLICIES.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {form.cancellation_policy === "custom" && (
                  <div className="mt-3">
                    <Textarea data-testid="ag-cancel-custom" rows={3} placeholder="Décrivez les conditions d'annulation personnalisées…" value={form.cancellation_custom_text} onChange={(e) => upd("cancellation_custom_text", e.target.value)} />
                  </div>
                )}
              </div>

              <div className="md:col-span-2 border-t pt-4">
                <div className="text-xs uppercase tracking-widest text-[#0e7a3f] font-bold mb-3">KYC — Documents & validation</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <F l="Statut KYC">
                    <Select value={form.kyc_status || "pending"} onValueChange={(v) => upd("kyc_status", v)}>
                      <SelectTrigger data-testid="ag-kyc-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">En attente</SelectItem>
                        <SelectItem value="approved">Approuvée</SelectItem>
                        <SelectItem value="rejected">Rejetée</SelectItem>
                      </SelectContent>
                    </Select>
                  </F>
                  <F l="Notes KYC (motif refus, remarques)"><Input value={form.kyc_notes || ""} onChange={(e) => upd("kyc_notes", e.target.value)} /></F>
                  <div><Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">RCCM (Registre du commerce)</Label><div className="mt-1.5"><ImageUpload value={form.kyc_rccm_url} onChange={(v) => upd("kyc_rccm_url", v)} label="RCCM" testId="ag-kyc-rccm" /></div></div>
                  <div><Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">RIB / Coordonnées bancaires</Label><div className="mt-1.5"><ImageUpload value={form.kyc_rib_url} onChange={(v) => upd("kyc_rib_url", v)} label="RIB" testId="ag-kyc-rib" /></div></div>
                  <div><Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">Pièce d'identité du gérant</Label><div className="mt-1.5"><ImageUpload value={form.kyc_id_doc_url} onChange={(v) => upd("kyc_id_doc_url", v)} label="Pièce d'identité" testId="ag-kyc-id" /></div></div>
                  <div><Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">Contrat de partenariat signé</Label><div className="mt-1.5"><ImageUpload value={form.kyc_partnership_contract_url} onChange={(v) => upd("kyc_partnership_contract_url", v)} label="Contrat" testId="ag-kyc-contract" /></div></div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={save} data-testid="save-agency" className="mbk-btn-primary text-white">Enregistrer</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 ? <div className="col-span-full bg-white rounded-2xl p-12 text-center border border-dashed">Aucune agence. Créez-en une pour commencer.</div> :
          items.map((a) => (
            <div key={a.id} className="bg-white rounded-2xl p-5 border border-gray-100 mbk-card-shadow">
              <div className="flex items-center gap-3">
                {a.logo_url ? <img src={a.logo_url} className="h-12 w-12 rounded-full object-cover" alt="" /> :
                  <div className="h-12 w-12 rounded-full bg-[#0a1e3f] text-[#d4a437] font-black flex items-center justify-center">{a.name?.[0]}</div>}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#0a1e3f] truncate">{a.name}</div>
                  <div className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> {a.city}, {a.country}</div>
                </div>
                {(() => {
                  const meta = KYC_META[a.kyc_status || "pending"];
                  const Icon = meta.icon;
                  return (
                    <span data-testid={`ag-kyc-badge-${a.id}`} className={`text-[10px] font-bold px-2 py-1 rounded-full inline-flex items-center gap-1 ${meta.color}`}>
                      <Icon className="h-3 w-3" /> {meta.label}
                    </span>
                  );
                })()}
              </div>
              <div className="mt-3 text-xs text-gray-500 line-clamp-2">{a.description}</div>
              <div className="mt-3 flex items-center justify-between">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-xs font-bold text-[#0e7a3f]">{a.vehicle_count} véhicule{a.vehicle_count > 1 ? "s" : ""}</span>
                  {a.rating > 0 && (
                    <span className="text-xs font-bold text-[#0a1e3f] inline-flex items-center gap-1" data-testid={`admin-ag-rating-${a.id}`}>
                      <Star className="h-3 w-3 fill-[#d4a437] text-[#d4a437]" />
                      {Number(a.rating).toFixed(1)}
                      <span className="text-gray-400 font-normal">({a.reviews_count || 0})</span>
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Link to={`/admin/agencies/${a.id}/detail`} data-testid={`ag-detail-${a.id}`} className="h-8 px-3 rounded-full bg-[#0e7a3f]/10 hover:bg-[#0e7a3f]/20 text-[#0e7a3f] text-[10px] font-bold inline-flex items-center gap-1">Partenariat</Link>
                  <button onClick={() => edit(a)} className="h-8 w-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => remove(a.id)} className="h-8 w-8 rounded-full bg-red-50 hover:bg-red-100 text-red-600 flex items-center justify-center"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

const F = ({ l, children }) => (<div><Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">{l}</Label><div className="mt-1.5">{children}</div></div>);
